// wizard.ts — capturer onboarding: requirements → install → bind → headset → sync.
//
// Shown INSTEAD of the Portal UI for non-admin users whose profile
// onboarding_step !== 'done' ("super simple" is the requirement; admins can
// inspect it via #wizard). The account step (name/password/ToS) already
// happened in auth.ts before this renders. State persists in
// profiles.onboarding_step + onboarding_progress, and BOTH surfaces render
// from that one row:
//
//   browser-owned   requirements  self-attest checklist (dev mode, cable…)
//                   connect       WebADB with hand-holding
//                   install       Capture APK from R2 over WebADB (EGO-96)
//                   bind          TV-style pair code (EGO-97/99)
//   headset-owned   hs_permissions / hs_intro / hs_task
//                   — the wizard turns MIRROR: it renders live headset
//                   progress from the realtime subscription on our own
//                   profiles row and asks for nothing but "put the headset
//                   on". Headset writes land via worker /onboarding/event;
//                   presence = progress.hs_seen_at (stamped by the headset's
//                   state poll).
//   browser-owned   capture       "Sync": pull → QA gate → PII mask burn-in →
//                   upload (stored step name kept from EGO-99 so existing
//                   rows resume).
//
// The invariant every surface honors: render whatever the row says, even when
// the step moves BACKWARD (redo links, admin strip, QA retry) — progress is
// never assumed monotonic.
import type { Adb } from "@yume-chan/adb";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase, getProfile } from "./auth.ts";
import { WORKER_URL, latestRelease, installedBuild, downloadApk, installApk } from "./apk.ts";
import { listRemote, pullSession, recRoot } from "./pull.ts";
import { handleWriter } from "./fsdir.ts";
import { uploadSession } from "./upload.ts";
import { transcribeInto } from "./transcribe.ts";
import { runQa } from "./qa.ts";
import { maskBurnInto } from "./maskburn.ts";

const PKG = "com.Fluid.XRBase";
const STEPS = ["requirements", "connect", "install", "bind",
               "hs_permissions", "hs_intro", "hs_task", "capture"] as const;
type Step = typeof STEPS[number] | "done";

// Within-step detail shared with the headset (see worker /onboarding/event).
type Progress = {
  permissions?: Record<string, boolean>;
  substep?: number;        // # of task substeps completed; present = recording started
  session_id?: string;     // recorder id of the finished task recording
  task_done_at?: string;
  retry?: string;          // QA-retry note shown in-headset (M4)
  hs_seen_at?: string;     // presence heartbeat
};

// Keys mirror the menu page's OB_PERM_KEYS mapping of HudPermissions labels.
// Display labels MUST stay word-for-word identical to PERM_FRIENDLY in
// capture/Assets/StreamingAssets/menu/index.html — this mirror and the
// in-headset gate narrate the same list.
const PERMS: [string, string][] = [
  ["camera", "Headset camera (passthrough recording)"],
  ["mic", "Microphone (your narration)"],
  ["hands", "Hand tracking"],
  ["body", "Body tracking"],
  ["scene", "Room scene data"],
  ["anchors", "Room anchors"],
];
const SUBSTEPS = [
  "Pick up both glasses",
  "Pour the water from one glass into the other",
  "Set both glasses down",
];
const PRESENCE_FRESH_MS = 30_000;

export interface WizardHost {
  /** current adb connection, if any */
  getAdb(): Adb | undefined;
  /** run the interactive WebUSB connect flow; resolves to the connection or null */
  connect(): Promise<Adb | null>;
  /** called when onboarding completes so the shell can swap to the full app */
  onDone(): void;
}

const $ = (id: string) => document.getElementById(id)!;

let host: WizardHost;
let step: Step = "requirements";
let progress: Progress = {};
let uid = "";
let busy = false;
let pendingRender = false;
let channel: RealtimeChannel | null = null;
let presenceTimer: number | null = null;

export async function startWizard(h: WizardHost, onboardingStep: string) {
  host = h;
  // A finished account revisiting (footer "Setup guide" → #wizard) lands on
  // the last step with every redo link available; the row isn't touched until
  // the user actually redoes something.
  step = onboardingStep === "done" ? "capture"
       : (STEPS as readonly string[]).includes(onboardingStep)
    ? onboardingStep as Step : "requirements";
  uid = (await supabase.auth.getUser()).data.user!.id;
  const { data } = await supabase.from("profiles")
    .select("onboarding_progress").eq("id", uid).single();
  progress = (data?.onboarding_progress as Progress) ?? {};
  subscribe();
  render();
}

// The mirror: headset-owned steps advance via worker writes to our profiles
// row; this subscription is how the wizard sees them (~instantly).
function subscribe() {
  channel?.unsubscribe();
  channel = supabase
    .channel(`onboarding-${uid}`)
    .on("postgres_changes",
      { event: "UPDATE", schema: "public", table: "profiles", filter: `id=eq.${uid}` },
      (payload) => {
        const row = payload.new as { onboarding_step?: string; onboarding_progress?: Progress };
        if (row.onboarding_progress) progress = row.onboarding_progress;
        const next = row.onboarding_step;
        if (next && next !== step) {
          if (next === "done") { cleanup(); host.onDone(); return; }
          if ((STEPS as readonly string[]).includes(next)) step = next as Step;
        }
        scheduleRender();
      })
    .subscribe();
}

// A step's run() can take minutes (APK download, session pull) — never let a
// realtime poke replace the DOM under it; re-render when it finishes.
function scheduleRender() {
  if (busy) { pendingRender = true; return; }
  render();
}

function cleanup() {
  channel?.unsubscribe();
  channel = null;
  if (presenceTimer != null) { clearInterval(presenceTimer); presenceTimer = null; }
}

async function saveStep(next: Step, newProgress?: Progress) {
  step = next;
  if (newProgress) progress = newProgress;
  const patch: Record<string, unknown> = { onboarding_step: next };
  if (newProgress) patch.onboarding_progress = newProgress;
  await supabase.from("profiles").update(patch).eq("id", uid);
  if (next === "done") { cleanup(); host.onDone(); return; }
  render();
}

function setMsg(text: string, err = false) {
  const el = document.getElementById("wizMsg");
  if (el) { el.textContent = text; el.style.color = err ? "var(--err)" : "var(--muted)"; }
}

// ── step bodies ──────────────────────────────────────────────────────────────

// A step may declare a secondary action: place a <button id="wizAlt"> anywhere
// in its html (so it sits where it belongs in the instructions) and implement
// altRun. render() wires it with the same busy-guard as the primary button.
// Mirror steps (headset-owned) have no button at all — the headset drives.
const BODIES: Record<Exclude<Step, "done">, {
  title: string; html: () => string;
  button?: string;
  run?: () => Promise<void>;
  altRun?: () => Promise<void>;
  mirror?: boolean;
  /** shows a "skip this step" link — for steps that may already be done
      (e.g. the app was installed on another machine or by an admin) */
  skippable?: boolean;
}> = {
  requirements: {
    title: "Before you start",
    html: () => {
      const chromeOk = "usb" in navigator;
      const box = (id: string, label: string) => `
        <label style="display:flex; gap:8px; align-items:flex-start; font-size:13px; cursor:pointer;">
          <input type="checkbox" id="${id}" class="reqBox" style="margin-top:3px;"> <span>${label}</span>
        </label>`;
      return `<p>Everything here is required — the next steps fail without them.</p>
      <div style="display:flex; flex-direction:column; gap:8px; margin:10px 0;">
        ${box("reqDev", `<b>Developer mode is on</b> for this Quest — set up with a Meta developer
          account in the Meta Horizon phone app. If you've sideloaded before, you're set.`)}
        ${box("reqCharge", `The headset is <b>charged</b> and you have a <b>USB-C cable</b> that
          reaches this computer.`)}
        ${box("reqSpace", `You have a <b>clear table</b>, two glasses, and can <b>speak out loud</b>
          while recording — you'll narrate what you do; that's the point of the data.`)}
        <div style="display:flex; gap:8px; align-items:flex-start; font-size:13px;">
          <span style="margin-top:1px; color:${chromeOk ? "var(--ok)" : "var(--err)"}">${chromeOk ? "✓" : "✗"}</span>
          <span>${chromeOk
            ? "This browser supports WebUSB — you're good."
            : "This browser can't talk to the headset — <b>use desktop Chrome or Edge</b>, then come back."}</span>
        </div>
      </div>`;
    },
    button: "Continue",
    run: async () => { await saveStep("connect"); },
  },

  connect: {
    title: "Connect your headset",
    html: () => `<ol>
      <li>Plug the Quest into this computer with a USB-C cable.</li>
      <li>Quit anything else that talks to the headset — <b>SideQuest</b>, <b>Meta Quest
        Developer Hub</b>, or a terminal where you've run <b>adb</b> — only one thing
        can hold the USB connection, and this page needs it.</li>
      <li>Click Connect and pick the Quest in the browser dialog.</li>
    </ol>
    <div id="adbConflict" style="display:none; gap:10px; align-items:flex-start; margin:10px 0 4px;
         padding:12px 14px; border:1px solid var(--err); border-radius:8px; background:var(--panel);
         font-size:14.5px; line-height:1.5; color:var(--text);">
      <span style="font-size:20px;">⚠️</span>
      <span><b>Something else is probably holding the headset's USB connection.</b><br>
      Quit <b>SideQuest</b> and <b>Meta Quest Developer Hub</b>, and if you've ever used
      <b>adb</b> in a terminal, run:<br>
      <code style="display:inline-block; margin:6px 0; padding:4px 10px; border-radius:6px;
        background:var(--panel2); font-size:14px;">adb kill-server</code><br>
      Then click <b>Connect headset</b> again.</span>
    </div>
    <div style="display:flex; gap:10px; align-items:flex-start; margin:10px 0 4px; padding:12px 14px;
         border:1px solid var(--accent); border-radius:8px; background:var(--panel);
         font-size:14.5px; line-height:1.5; color:var(--text);">
      <span style="font-size:20px;">🥽</span>
      <span><b>Then put the headset on</b> — an <b>"Allow USB debugging?"</b> prompt is waiting
      inside. Check <b>Always allow</b> and accept it. This page waits until you do.</span>
    </div>`,
    button: "Connect headset",
    skippable: true,
    run: async () => {
      const already = host.getAdb();
      if (!already) {
        setMsg("Waiting… pick the Quest in the browser dialog, then PUT THE HEADSET ON and accept the \"Allow USB debugging?\" prompt.");
      }
      // Any failure here is overwhelmingly an adb-conflict (SideQuest / MQDH /
      // a terminal adb server owns the device) — reveal the how-to-fix box.
      const conflict = document.getElementById("adbConflict");
      let adb;
      try { adb = already ?? await host.connect(); }
      catch (e) { if (conflict) conflict.style.display = "flex"; throw e; }
      if (!adb) {
        if (conflict) conflict.style.display = "flex";
        throw new Error("no device connected — if the cable is good, something else is holding adb (see the box above)");
      }
      await saveStep("install");
    },
  },

  install: {
    title: "Install the Capture app",
    html: () => `<p>This downloads the Capture app and installs it on the headset over the cable.
      Nothing to do in the headset — just leave it plugged in.</p>`,
    button: "Install Capture",
    skippable: true,
    run: async () => {
      const adb = host.getAdb();
      if (!adb) { await saveStep("connect"); throw new Error("headset disconnected — reconnect first"); }
      const rel = await latestRelease();
      if (!rel) throw new Error("no app release published — contact Egocentric");
      const inst = await installedBuild(adb, PKG);
      if (inst != null && inst >= rel.build) { setMsg(`already installed (build ${inst})`); await saveStep("bind"); return; }
      setMsg(`downloading v${rel.version} (${(rel.size / 1048576) | 0} MB)…`);
      const apk = await downloadApk(rel, (done, total) => setMsg(`downloading… ${((done / total) * 100) | 0}%`));
      setMsg("installing on the headset…");
      await installApk(adb, apk, (phase) => setMsg(phase));
      await saveStep("bind");
    },
  },

  bind: {
    // TV-style pairing (EGO-99): the headset displays a code in its menu; the
    // user types it here. No adb delivery — the earlier file-mailbox approach
    // lost repeatedly to scoped-storage ownership rules; this can't.
    title: "Link the headset to your account",
    html: () => `<ol>
      <li>Start the Capture app on the headset:
        <div style="margin:8px 0 2px;">
          <button id="wizAlt" class="secondary">Launch Capture on headset</button>
        </div>
        <span style="font-size:12px;">(or just open Capture from the headset's library)</span>
      </li>
      <li><b>Put the headset on.</b> The Capture screen shows a 6-character code.</li>
      <li>Type that code here and click <b>Link headset</b>:
        <div style="margin:14px 0 4px;">
          <input id="pairCode" type="text" placeholder="ABC479" maxlength="6"
            autocomplete="off" spellcheck="false" autocapitalize="characters"
            style="display:block; width:100%; max-width:340px; box-sizing:border-box;
                   padding:16px 12px; border-radius:12px; text-align:center;
                   border:2px solid var(--line); background:var(--panel2); color:var(--text);
                   font-family:ui-monospace,Menlo,monospace; font-size:40px; font-weight:700;
                   letter-spacing:0.25em; text-transform:uppercase;">
        </div>
      </li>
      </ol>`,
    button: "Link headset",
    skippable: true,
    altRun: async () => {
      const adb = host.getAdb();
      if (!adb) { await saveStep("connect"); throw new Error("headset disconnected — reconnect first"); }
      await adb.subprocess.spawnAndWaitLegacy(`monkey -p ${PKG} -c android.intent.category.LAUNCHER 1`);
      setMsg("Capture launched — put the headset on and read the code from the banner");
    },
    run: async () => {
      const input = (document.getElementById("pairCode") as HTMLInputElement | null)?.value ?? "";
      const code = input.trim().toUpperCase().replace(/[\s-]/g, "");
      if (!code) throw new Error("type the code shown in the headset's Capture menu first");
      if (!/^[A-HJ-KM-NP-Z2-9]{6}$/.test(code))
        throw new Error("that doesn't look like a pairing code — it's the 6 letters/digits shown on the headset");

      const before = new Date().toISOString();
      setMsg("claiming the headset…");
      const { data: { session } } = await supabase.auth.getSession();
      // USB serial (when still cabled) rides along for the headsets list;
      // the dedup key (ANDROID_ID) comes from the headset at pair-status.
      const serial = host.getAdb()?.serial ?? null;
      const r = await fetch(`${WORKER_URL}/device/claim`, {
        method: "POST",
        headers: { Authorization: `Bearer ${session!.access_token}`, "content-type": "application/json" },
        body: JSON.stringify({ code, serial }),
      });
      if (r.status === 409) throw new Error("that code was already used — restart Capture on the headset for a fresh one");
      if (!r.ok) throw new Error(`claim failed: ${r.status}`);

      setMsg("waiting for the headset to pick it up… (takes a few seconds)");
      for (let i = 0; i < 20; i++) {
        await new Promise((res) => setTimeout(res, 3000));
        const { data } = await supabase.from("devices")
          .select("id, last_seen_at").is("revoked_at", null).gte("created_at", before).not("last_seen_at", "is", null).limit(1);
        if (data?.length) { await saveStep("hs_permissions"); return; }
      }
      throw new Error("the headset hasn't picked up the claim — is it on wifi? check the code and retry with a fresh one");
    },
  },

  // ── headset-owned steps: the wizard is a live mirror, nothing to click ─────

  hs_permissions: {
    title: "In the headset — allow permissions",
    mirror: true,
    html: () => {
      // Still-needed permissions on top; granted ones sink greyed below a
      // hairline divider — what's left to approve always reads first.
      const pending = PERMS.filter(([key]) => !progress.permissions?.[key]);
      const granted = PERMS.filter(([key]) => progress.permissions?.[key]);
      const row = ([, label]: [string, string], ok: boolean) =>
        `<div style="font-size:13px; ${ok ? "opacity:.4;" : ""}">
           <span style="color:${ok ? "var(--ok)" : "var(--muted)"}">${ok ? "✓" : "○"}</span> ${label}</div>`;
      return `${presenceBanner()}
      <p><b>Put the headset on.</b> Capture asks for the permissions it needs to record —
      approve each one. This page follows along as you go:</p>
      <div style="display:flex; flex-direction:column; gap:4px; margin:8px 0;">
        ${pending.map((p) => row(p, false)).join("")}
        ${pending.length && granted.length
          ? `<div style="border-top:1px solid var(--line); margin:6px 0;"></div>` : ""}
        ${granted.map((p) => row(p, true)).join("")}
      </div>`;
    },
  },

  hs_intro: {
    title: "In the headset — how capturing works",
    mirror: true,
    html: () => `${presenceBanner()}
      <p>Capture is showing a short video of your first task: pouring water between two
      glasses while narrating. Watch it through — it shows exactly what a good capture
      looks and <i>sounds</i> like.</p>`,
  },

  hs_task: {
    title: "In the headset — your first capture",
    mirror: true,
    html: () => {
      const n = progress.substep;
      const started = typeof n === "number";
      const retry = progress.retry
        ? `<div style="margin:8px 0; padding:8px 12px; border:1px solid var(--warn); border-radius:6px; font-size:12.5px; color:var(--warn);">${progress.retry}</div>`
        : "";
      return `${presenceBanner()}${retry}
      <p>Set two glasses on the table, one with some water. Click <b>Start the task</b> in
      the headset, narrate what you're doing out loud, and say <b>"Next Step"</b> as you
      finish each step:</p>
      <div style="display:flex; flex-direction:column; gap:4px; margin:8px 0;">
        ${SUBSTEPS.map((label, i) => {
          const mark = started && i < n! ? `<span style="color:var(--ok)">✓</span>`
                     : started && i === n ? `<span>→</span>`
                     : `<span style="color:var(--muted)">○</span>`;
          const dim = started && i === n ? "" : "opacity:.7;";
          return `<div style="font-size:13px; ${dim}">${mark} ${i + 1}. ${label}</div>`;
        }).join("")}
      </div>
      <div style="font-size:12px; color:var(--muted);">${
        started ? "● Recording — keep narrating, take your time." : "Recording hasn't started yet."
      }</div>`;
    },
  },

  // Stored step name is still "capture" (EGO-99) so pre-existing accounts
  // resume cleanly; the UI calls it Sync — the recording already happened
  // in-headset during hs_task.
  capture: {
    title: "Sync your recording",
    html: () => `<p>Nice work — the task recording is saved on the headset. Now bring it home:</p>
      <ol>
      <li>Take the headset off and plug it into this computer.</li>
      <li>Click <b>Sync</b>. We pull the recording, run quality checks, and upload it.</li>
    </ol>
    <div id="syncPanel" style="display:none;"></div>`,
    button: "Sync",
    skippable: true,
    run: async () => {
      const adb = host.getAdb() ?? await host.connect();
      if (!adb) { await saveStep("connect"); throw new Error("headset disconnected — reconnect first"); }
      setMsg("looking for your recording…");
      const sessions = await listRemote(adb, recRoot(PKG));
      if (!sessions.length) {
        // No redo links in the one-step view — move the flow back ourselves.
        await saveStep("hs_task", progressForJump("hs_task"));
        throw new Error("no recording found on the headset — put it on and record the task again");
      }
      // The task_done event tells us exactly which session to pull; fall back
      // to newest for old flows / manual recordings.
      const target = (progress.session_id && sessions.find((s) => s.id === progress.session_id))
        || sessions[sessions.length - 1];

      setMsg("");
      // ONE 0–100 bar for the whole sync (not per phase): pull 0–30, quality
      // checks + transcription hold at 30, PII mask burn-in 30–60 (a full
      // decode+re-encode of both eyes — the most expensive leg on a slow
      // machine, and a no-op when the session has no masks), checksumming
      // 60–65, upload 65–100. The label says what's happening; the bar never
      // resets or lingers at a stale 100.
      const span = (lo: number, hi: number, done: number, total: number) =>
        lo + (done / Math.max(1, total)) * (hi - lo);
      paintSync("pull", 0, `found ${target.id} — ${mb(target.bytes)}`);
      const opfs = await navigator.storage.getDirectory();
      const res = await pullSession(adb, PKG, target.id, handleWriter(opfs), {
        deleteRemote: false,
        onProgress: (p) => {
          if (p.phase === "pull")
            paintSync("pull", span(0, 30, p.bytesDone, p.bytesTotal),
              `${mb(p.bytesDone)} / ${mb(p.bytesTotal)}`);
          else if (p.phase === "verify")
            paintSync("pull", 30, "verifying every byte made it…");
        },
      });
      if (!res.verified) throw new Error(`pull verification failed: ${res.mismatches.join(", ")}`);

      // Provenance: the session must be stamped with THIS user (EGO-97/98).
      const dir = await opfs.getDirectoryHandle(target.id);
      const meta = JSON.parse(await (await (await dir.getFileHandle("meta.json")).getFile()).text());
      if (meta.user_id !== uid) {
        await saveStep("bind", progressForJump("bind"));
        throw new Error("this recording isn't stamped with your account — link the headset again, then record again");
      }

      // The QA gate is near-instant, which reads as "skipped" — hold the step
      // on screen briefly so the user registers that their capture was checked.
      paintSync("qa", 30, "running quality checks…");
      const qaStart = Date.now();
      const cert = await runQa(dir);
      const dwell = 1500 - (Date.now() - qaStart);
      if (dwell > 0) await new Promise((r) => setTimeout(r, dwell));
      const certEl = $("wizCert");
      certEl.style.display = "";
      certEl.innerHTML = renderCertificate(cert);
      if (cert.verdict === "FAIL") {
        // The one backward arrow in the machine: a failed QA sends the flow
        // back to the headset task with a retry note (shown on both surfaces)
        // instead of dead-ending here.
        const why = (cert.blockers ?? [])[0];
        const p = progressForJump("hs_task");
        p.retry = `Your last recording didn't pass the quality check${why ? ` (${why})` : ""} — let's do it once more.`;
        await saveStep("hs_task", p);
        throw new Error("the capture failed quality checks — put the headset back on and redo the task, then Sync again");
      }

      // Transcribe at ingest (EGO-132 B): STT the narration before upload so
      // the session lands in R2 already transcribed. Non-fatal — a hiccup here
      // must never strand a capture that already passed QA.
      try {
        paintSync("qa", 30, "transcribing your narration…");
        await transcribeInto(dir, { log: () => {}, onProgress: (i, n) =>
          paintSync("qa", 30, `transcribing your narration — ${i + 1}/${n}…`) });
      } catch { /* upload proceeds without a transcript */ }

      // PII mask burn-in (EGO-163): black out the wearer's marked privacy
      // regions in left/right.mp4 before the upload. NOT best-effort, unlike the
      // transcription above — if we can't mask a masked session, nothing about
      // it leaves this machine (upload.ts's preflight refuses it too).
      try {
        paintSync("mask", 30, "hiding the private areas you marked…");
        await maskBurnInto(dir, { log: () => {}, onProgress: (frac) =>
          paintSync("mask", 30 + frac * 30,
            `hiding the private areas you marked — ${Math.round(frac * 100)}%`) });
      } catch (e: any) {
        throw new Error(
          `we couldn't hide the private areas you marked, so this recording wasn't uploaded: ${e?.message ?? e}`,
          { cause: e },
        );
      }

      paintSync("upload", 60, "preparing…");
      await uploadSession(opfs, target.id, {
        isTest: true,
        onProgress: (p) => {
          if (p.phase === "hash")
            paintSync("upload", span(60, 65, p.bytesDone, p.bytesTotal), "checksumming before upload…");
          else if (p.phase === "upload")
            paintSync("upload", span(65, 100, p.bytesDone, p.bytesTotal),
              `${mb(p.bytesDone)} / ${mb(p.bytesTotal)}`);
          else if (p.phase === "commit") paintSync("upload", 100, "finalizing…");
        },
      });
      paintSync("done", 100, "all synced");
      setMsg("");
      step = "done";
      await supabase.from("profiles").update({ onboarding_step: "done" }).eq("id", uid);
      cleanup();
      celebrate();
      setTimeout(() => host.onDone(), 2600);
    },
  },
};

// ── Sync progress panel ──────────────────────────────────────────────────────
// The Sync step moves gigabytes; the state has to be LOUD. A phase checklist
// (pull → checks → mask → upload) over a fat progress bar with a big % readout.
// Rebuilds the DOM only on phase change; per-chunk events just move the bar.
const SYNC_PHASES: [string, string][] = [
  ["pull", "Pull the recording off the headset"],
  ["qa", "Quality checks"],
  ["mask", "Hide private areas"],
  ["upload", "Upload"],
];
let syncPhasePainted = "";
function paintSync(phase: string, pct: number | null, detail: string) {
  const panel = document.getElementById("syncPanel");
  if (!panel) return;
  panel.style.display = "block";
  // Rebuild on phase change, or when a re-render left the panel empty (redo/retry).
  if (syncPhasePainted !== phase || !document.getElementById("syncBar")) {
    syncPhasePainted = phase;
    const idx = SYNC_PHASES.findIndex(([k]) => k === phase);
    panel.innerHTML = `
      <div style="display:flex; flex-direction:column; gap:6px; margin:16px 0 14px;">
        ${SYNC_PHASES.map(([k, label], i) => {
          const state = phase === "done" || i < idx ? "done" : i === idx ? "now" : "todo";
          const mark = state === "done" ? `<span style="color:var(--ok)">✓</span>`
                     : state === "now" ? "<span>→</span>"
                     : `<span style="color:var(--muted)">○</span>`;
          const style = state === "now" ? "font-weight:600;" : state === "todo" ? "opacity:.5;" : "opacity:.75;";
          return `<div style="font-size:14px; ${style}">${mark} ${label}</div>`;
        }).join("")}
      </div>
      <div style="max-width:420px; height:18px; border-radius:9px; background:var(--panel2); border:1px solid var(--line); overflow:hidden;">
        <div id="syncBar" style="height:100%; width:0%; background:var(--accent); transition:width .12s linear;"></div>
      </div>
      <div style="display:flex; justify-content:space-between; align-items:baseline; margin-top:8px; max-width:420px;">
        <span id="syncPct" style="font-size:26px; font-weight:700; font-variant-numeric:tabular-nums;"></span>
        <span id="syncDetail" style="font-size:13px; color:var(--muted);"></span>
      </div>`;
  }
  const bar = document.getElementById("syncBar");
  const pctEl = document.getElementById("syncPct");
  const detailEl = document.getElementById("syncDetail");
  if (bar) bar.style.width = `${pct ?? 100}%`;
  if (bar && pct == null) bar.style.opacity = "0.35";   // indeterminate (QA etc.)
  else if (bar) bar.style.opacity = "1";
  if (pctEl) pctEl.textContent = pct == null ? "…" : `${pct | 0}%`;
  if (detailEl) detailEl.textContent = detail;
}
const mb = (n: number) => `${(n / 1048576) | 0} MB`;

// A beat of celebration between the last upload and the app swap — saveStep
// ("done") tears the wizard down, so this paints over it and onDone's reload
// (main.ts) lands the user in the real Portal.
function celebrate() {
  const wiz = $("wizard");
  wiz.innerHTML = `<div style="text-align:center; padding:60px 0;">
    <div style="font-size:44px;">🎉</div>
    <h2 style="font-size:18px; margin:14px 0 6px;">You're all set</h2>
    <p style="font-size:13px; color:var(--muted); margin:0;">Your first capture passed the
    quality checks and is uploaded. Loading your dashboard…</p>
  </div>`;
}

// Presence: the headset's onboarding poll stamps progress.hs_seen_at through
// the worker; fresh = it's alive and showing the current step.
function presenceBanner(): string {
  const seen = progress.hs_seen_at ? Date.parse(progress.hs_seen_at) : 0;
  const online = seen > Date.now() - PRESENCE_FRESH_MS;
  return `<div style="display:inline-flex; align-items:center; gap:7px; font-size:12px; margin-bottom:8px;
      padding:3px 10px; border-radius:999px; border:1px solid var(--line); background:var(--panel2);
      color:${online ? "var(--ok)" : "var(--muted)"};">
    <span style="font-size:9px;">●</span>
    ${online ? "Headset connected" : "Waiting for the headset — is it on and connected to wifi?"}
  </div>`;
}

function renderCertificate(cert: any): string {
  const color = cert.verdict === "PASS" ? "var(--ok)" : cert.verdict === "WARN" ? "var(--warn)" : "var(--err)";
  const items = [...(cert.blockers ?? []).map((b: string) => `<li style="color:var(--err)">${b}</li>`),
                 ...(cert.warnings ?? []).map((w: string) => `<li style="color:var(--warn)">${w}</li>`)];
  return `<div style="border:1px solid var(--line); border-radius:8px; padding:12px 16px; background:var(--panel);">
    <b style="color:${color}">Quality check: ${cert.verdict ?? "?"}</b>
    ${items.length ? `<ul style="margin:8px 0 0; padding-left:18px; font-size:12px;">${items.join("")}</ul>`
                   : `<div style="font-size:12px; color:var(--muted); margin-top:4px;">All checks passed — this is exactly what a good capture looks like.</div>`}
  </div>`;
}

// Going back to a headset step starts a fresh task attempt — stale substep /
// session pointers would confuse both the mirror and the Sync step.
function progressForJump(target: Step): Progress {
  const p = { ...progress };
  if (target === "hs_task" || target === "hs_intro" || target === "hs_permissions"
      || STEPS.indexOf(target as any) <= STEPS.indexOf("bind")) {
    delete p.substep; delete p.task_done_at; delete p.session_id; delete p.retry;
  }
  return p;
}

// Admin-only debug chip, pinned top-right: jump anywhere, reset everything.
// Rendering always follows the row, so jumps are safe by construction — the
// headset re-syncs from its next state poll. (The user-facing view shows ONLY
// the current step, so this is also how admins move backward.)
function debugStrip(): string {
  // Admins always get it. `localStorage.egoDebug = "1"` shows it on any
  // account for THIS browser only — lets us drive the non-admin walkthrough
  // account without granting it admin (the mock signup exposes that account,
  // so it must never be admin).
  if (!getProfile()?.is_admin && localStorage.getItem("egoDebug") !== "1") return "";
  const opts = [...STEPS, "done"].map((s) =>
    `<option value="${s}" ${s === step ? "selected" : ""}>${s}</option>`).join("");
  return `<div style="position:fixed; top:10px; right:12px; z-index:50; display:flex; gap:6px; align-items:center;
      padding:5px 9px; border:1px dashed var(--warn); border-radius:8px; font-size:11px; color:var(--warn);
      background:var(--panel);">
    <b>admin</b>
    <select id="dbgStep" style="background:var(--panel2); color:var(--text); border:1px solid var(--line); border-radius:5px; padding:2px 5px; font-size:11px;">${opts}</select>
    <button id="dbgGo" class="secondary" style="font-size:11px; padding:2px 8px;">Jump</button>
    <a href="#wizard" id="dbgReset" style="color:var(--warn);">reset</a>
  </div>`;
}

// ── render ───────────────────────────────────────────────────────────────────

function render() {
  const wiz = $("wizard");
  wiz.style.display = "";
  const stepIdx = STEPS.indexOf(step as any);
  const b = BODIES[step as Exclude<Step, "done">];
  if (!b) return;
  // One step at a time: a centered card with a "3 / 8" counter — the full
  // list overwhelmed (8 steps!). Admins jump via the top-right debug chip.
  wiz.innerHTML = `${debugStrip()}
    <div style="display:flex; flex-direction:column; justify-content:center; min-height:76vh;">
      <div style="text-align:center; font-size:12px; letter-spacing:0.14em; color:var(--muted);
           text-transform:uppercase;">Step ${stepIdx + 1} of ${STEPS.length}</div>
      <h2 style="font-size:22px; margin:8px 0 0; text-align:center;">${b.title}</h2>
      ${stepIdx === 0 ? `<p style="font-size:12.5px; color:var(--muted); margin:8px 0 0; text-align:center;">
        About fifteen minutes end to end. You can close this page any time — it resumes where you left off.</p>` : ""}
      <div style="font-size:14px; margin:22px 0 6px;">${b.html()}</div>
      <div style="text-align:center; margin-top:12px;">
        ${b.button ? `<button id="wizGo" style="font-size:15px; padding:10px 28px;">${b.button}</button>` : ""}
        ${b.skippable
          ? `<a href="#" id="wizSkip" style="font-size:12px; color:var(--accent); margin-left:12px;">skip — already done</a>` : ""}
      </div>
      <div id="wizMsg" style="margin-top:10px; font-size:12.5px; color:var(--muted); text-align:center;"></div>
      <div id="wizCert" style="display:none; margin-top:10px;"></div>
    </div>`;
  // Skip advances without running the step; later steps re-route back to
  // connect themselves if the adb connection turns out to be needed.
  const skipEl = document.getElementById("wizSkip");
  if (skipEl) skipEl.onclick = (e) => {
    e.preventDefault();
    if (!busy) saveStep(STEPS[stepIdx + 1] ?? "done");
  };
  // Primary + optional secondary action share one busy-guard: a step's run()
  // can re-render (saveStep), so re-read the button before re-enabling.
  const wire = (id: string, fn?: () => Promise<void>) => {
    const b = document.getElementById(id) as HTMLButtonElement | null;
    if (!b || !fn) return;
    b.onclick = async () => {
      if (busy) return;
      busy = true; b.disabled = true;
      try { await fn(); }
      catch (e: any) { setMsg(e?.message ?? String(e), true); }
      finally {
        busy = false;
        const live = document.getElementById(id) as HTMLButtonElement | null;
        if (live) live.disabled = false;
        if (pendingRender) { pendingRender = false; render(); }
      }
    };
  };
  const body = BODIES[step as Exclude<Step, "done">];
  wire("wizGo", body?.run && (() => body.run!()));
  wire("wizAlt", body?.altRun && (() => body.altRun!()));

  // Bind: Enter in the code field = clicking Link headset (same busy-guard via
  // the button's handler; run() still validates the 6 chars).
  if (step === "bind") {
    const codeEl = document.getElementById("pairCode") as HTMLInputElement | null;
    codeEl?.focus();
    codeEl?.addEventListener("keydown", (e) => {
      if (e.key === "Enter") (document.getElementById("wizGo") as HTMLButtonElement | null)?.click();
    });
  }

  // Requirements: the Continue button unlocks only when every box is checked.
  if (step === "requirements") {
    const go = document.getElementById("wizGo") as HTMLButtonElement | null;
    const boxes = [...wiz.querySelectorAll<HTMLInputElement>(".reqBox")];
    const sync = () => { if (go) go.disabled = !boxes.every((b) => b.checked); };
    boxes.forEach((b) => b.addEventListener("change", sync));
    sync();
  }

  // Admin strip actions.
  const dbgGo = document.getElementById("dbgGo");
  if (dbgGo) dbgGo.onclick = () => {
    const target = (document.getElementById("dbgStep") as HTMLSelectElement).value as Step;
    if (!busy) saveStep(target, progressForJump(target));
  };
  const dbgReset = document.getElementById("dbgReset");
  if (dbgReset) dbgReset.onclick = (e) => {
    e.preventDefault();
    if (!busy) saveStep("requirements", {});
  };

  // Presence staleness only changes with time, not with row updates — tick the
  // mirror steps so "connected" honestly decays into "waiting".
  if (presenceTimer != null) { clearInterval(presenceTimer); presenceTimer = null; }
  if (body?.mirror) {
    presenceTimer = window.setInterval(() => { if (!busy) render(); }, 5000);
  }
}
